/**
 * Saved Searches Panel
 * SCR-106: Saved Searches & Search History
 *
 * Panel component displaying user's saved searches with CRUD operations.
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-106 Search Interface
 * @iso27001 A.18.1.4 Privacy and protection of PII
 */

import { useState } from 'react';
import { Icon, Spinner, Badge } from '@myvault/shared/components';
import {
  useSavedSearches,
  useCreateSavedSearch,
  useDeleteSavedSearch,
  type SavedSearch,
} from '../hooks/useSearchPersistence';

interface SavedSearchesPanelProps {
  onSelectSearch: (query: string, filters?: Record<string, unknown>) => void;
  currentQuery?: string;
}

export function SavedSearchesPanel({ onSelectSearch, currentQuery }: SavedSearchesPanelProps) {
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error } = useSavedSearches();
  const createMutation = useCreateSavedSearch();
  const deleteMutation = useDeleteSavedSearch();

  const handleSaveSearch = async () => {
    if (!currentQuery || currentQuery.length < 2) {
      setSaveError('Voer eerst een zoekopdracht in');
      return;
    }

    if (!saveName.trim()) {
      setSaveError('Naam is verplicht');
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: saveName.trim(),
        query: currentQuery,
      });
      setSaveName('');
      setShowSaveForm(false);
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
    }
  };

  const handleDeleteSearch = async (searchId: string) => {
    try {
      await deleteMutation.mutateAsync(searchId);
    } catch {
      // Error handling via toast would be better
      console.error('Failed to delete saved search');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
        <span className="ml-2 text-sm text-mv-gray-500">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-3 text-sm text-mv-red-600">
        Kon opgeslagen zoekopdrachten niet laden
      </div>
    );
  }

  const savedSearches = data?.items || [];

  return (
    <div className="border-t border-mv-gray-200">
      {/* Header with save button */}
      <div className="flex items-center justify-between px-4 py-2">
        <span className="text-sm font-medium text-mv-gray-500">
          Opgeslagen zoekopdrachten
        </span>
        {currentQuery && currentQuery.length >= 2 && (
          <button
            onClick={() => setShowSaveForm(!showSaveForm)}
            className="flex items-center gap-1 text-xs text-mv-blue-600 hover:text-mv-blue-700"
          >
            <Icon name="bookmark" variant="outline" size="xs" />
            Bewaar zoekopdracht
          </button>
        )}
      </div>

      {/* Save form */}
      {showSaveForm && (
        <div className="border-b border-mv-gray-200 bg-mv-gray-50 px-4 py-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={saveName}
              onChange={(e) => {
                setSaveName(e.target.value);
                setSaveError(null);
              }}
              placeholder="Naam voor deze zoekopdracht"
              className="flex-1 rounded-md border border-mv-gray-300 px-3 py-1.5 text-sm focus:border-mv-blue-500 focus:outline-none focus:ring-1 focus:ring-mv-blue-500"
              maxLength={100}
            />
            <button
              onClick={handleSaveSearch}
              disabled={createMutation.isPending}
              className="rounded-md bg-mv-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-mv-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Opslaan...' : 'Opslaan'}
            </button>
            <button
              onClick={() => {
                setShowSaveForm(false);
                setSaveName('');
                setSaveError(null);
              }}
              className="rounded-md border border-mv-gray-300 px-3 py-1.5 text-sm text-mv-gray-600 hover:bg-mv-gray-100"
            >
              Annuleren
            </button>
          </div>
          {saveError && (
            <p className="mt-1 text-xs text-mv-red-600">{saveError}</p>
          )}
          <p className="mt-1 text-xs text-mv-gray-400">
            Huidige zoekopdracht: "{currentQuery}"
          </p>
        </div>
      )}

      {/* Saved searches list */}
      {savedSearches.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <Icon
            name="bookmark"
            variant="outline"
            size="lg"
            className="mx-auto mb-2 text-mv-gray-300"
          />
          <p className="text-sm text-mv-gray-500">
            Nog geen opgeslagen zoekopdrachten
          </p>
          <p className="mt-1 text-xs text-mv-gray-400">
            Bewaar een zoekopdracht voor snelle toegang
          </p>
        </div>
      ) : (
        <ul className="max-h-[200px] overflow-y-auto">
          {savedSearches.map((search) => (
            <SavedSearchItem
              key={search.id}
              search={search}
              onSelect={() => onSelectSearch(search.query, search.filters || undefined)}
              onDelete={() => handleDeleteSearch(search.id)}
              isDeleting={deleteMutation.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface SavedSearchItemProps {
  search: SavedSearch;
  onSelect: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function SavedSearchItem({ search, onSelect, onDelete, isDeleting }: SavedSearchItemProps) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  return (
    <li className="group relative">
      <button
        onClick={onSelect}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-mv-gray-50"
      >
        <Icon
          name="bookmark"
          variant="solid"
          size="sm"
          className="text-mv-blue-500"
        />
        <div className="min-w-0 flex-1">
          <span className="block truncate font-medium text-mv-gray-900">
            {search.name}
          </span>
          <span className="block truncate text-xs text-mv-gray-500">
            {search.query}
          </span>
        </div>
        {search.filters && Object.keys(search.filters).length > 0 && (
          <Badge variant="default" className="text-xs">
            Filters
          </Badge>
        )}
      </button>

      {/* Delete button */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100">
        {showConfirmDelete ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                onDelete();
                setShowConfirmDelete(false);
              }}
              disabled={isDeleting}
              className="rounded bg-mv-red-600 px-2 py-1 text-xs text-white hover:bg-mv-red-700"
            >
              {isDeleting ? '...' : 'Ja'}
            </button>
            <button
              onClick={() => setShowConfirmDelete(false)}
              className="rounded bg-mv-gray-200 px-2 py-1 text-xs text-mv-gray-600 hover:bg-mv-gray-300"
            >
              Nee
            </button>
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowConfirmDelete(true);
            }}
            className="rounded p-1 text-mv-gray-400 hover:bg-mv-gray-100 hover:text-mv-red-600"
            aria-label={`Verwijder ${search.name}`}
          >
            <Icon name="trash" variant="outline" size="xs" />
          </button>
        )}
      </div>
    </li>
  );
}

export default SavedSearchesPanel;
